import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Global cache in memory + localStorage to prevent repeated API calls
const TRANSLATION_CACHE_KEY = 'sugarcane_translation_cache';
const getCachedTranslation = (text) => {
  try {
    const cache = JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY)) || {};
    return cache[text];
  } catch (e) {
    return null;
  }
};

const setCachedTranslation = (text, translatedText) => {
  try {
    const cache = JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY)) || {};
    cache[text] = translatedText;
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Ignore cache write errors
  }
};

// Check if string contains English characters (to avoid translating Marathi/Devanagari strings)
const containsEnglish = (text) => {
  return /[a-zA-Z]/.test(text);
};

export const translateTextOnline = async (text, targetLang = 'mr') => {
  if (!text || typeof text !== 'string') return text;
  if (!containsEnglish(text)) return text; // Already translated or not English

  const cached = getCachedTranslation(text);
  if (cached) return cached;

  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data && data[0]) {
      const translated = data[0].map(x => x[0]).join('');
      setCachedTranslation(text, translated);
      return translated;
    }
  } catch (e) {
    console.error('Translation error:', e);
  }
  return text;
};

const Translate = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);

  const rawText = typeof children === 'string' ? children : '';

  useEffect(() => {
    if (!rawText) {
      setTranslatedText(children);
      return;
    }

    // 1. Check if it's a defined key in our static translations
    const hasStaticKey = i18n.exists(rawText);
    if (hasStaticKey) {
      setTranslatedText(t(rawText));
      return;
    }

    // 2. If it's English and active language is Marathi (mr), translate it dynamically
    if (i18n.language === 'mr' && containsEnglish(rawText)) {
      const cached = getCachedTranslation(rawText);
      if (cached) {
        setTranslatedText(cached);
        return;
      }

      setLoading(true);
      translateTextOnline(rawText, 'mr').then(res => {
        setTranslatedText(res);
        setLoading(false);
      });
    } else {
      // 3. Otherwise render the original text
      setTranslatedText(rawText);
    }
  }, [rawText, children, i18n.language, t, i18n]);

  if (loading) {
    return <span className="opacity-70 animate-pulse">{rawText}</span>;
  }

  return <>{translatedText}</>;
};

export default Translate;
